import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, XCircle } from "lucide-react";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    api.get(`/auth/invite-info?token=${encodeURIComponent(token)}`)
      .then((res) => {
        setInfo(res.data);
        setState("ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/accept-invite", { token, name, password });
      setUser(res.data.user);
      toast.success(`Welcome to ${res.data.user.org.name}.`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <Card className="border shadow-none">
        {state === "loading" && (
          <CardContent className="py-14 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        )}
        {state === "invalid" && (
          <CardContent className="pt-8 pb-8 text-center" data-testid="accept-invite-invalid">
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--status-overdue)/0.12)]">
              <XCircle className="h-6 w-6 text-[hsl(var(--status-overdue))]" />
            </div>
            <h1 className="text-xl font-semibold">Invitation not found</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              This invitation is invalid, expired, or was already used. Ask your admin to send a new one.
            </p>
            <Link to="/login" className="block mt-6">
              <Button variant="secondary" className="w-full rounded-xl">Back to log in</Button>
            </Link>
          </CardContent>
        )}
        {state === "ready" && info && (
          <>
            <CardHeader>
              <CardTitle className="text-xl">Join {info.org_name}</CardTitle>
              <CardDescription>
                {info.invited_by ? `${info.invited_by} invited you` : "You've been invited"} to join as a{" "}
                <span className="font-medium text-foreground">{info.role}</span>. Set up your account to get started.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={info.email} disabled data-testid="accept-invite-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" required maxLength={100} value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Alex Rivera" data-testid="accept-invite-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Choose a password</Label>
                  <Input id="password" type="password" autoComplete="new-password" required minLength={8} maxLength={72}
                    value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
                    data-testid="accept-invite-password-input" />
                </div>
                <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="accept-invite-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join the team"}
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </AuthLayout>
  );
}
